// Shared utility functions for AI Analysis Result rendering

/**
 * Format endoteleon names from snake_case to Title Case
 */
export const formatEndoteleonName = (name: string): string => {
  return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Format biochemical marker names to uppercase
 */
export const formatBiochemicalName = (name: string): string => {
  return name.toUpperCase();
};

/**
 * Get color for uncertainty tag based on level
 */
export const getUncertaintyTagColor = (level: number): string => {
  if (level > 0.7) return 'red';
  if (level > 0.4) return 'orange';
  return 'green';
};

/**
 * Get color for direction tag
 */
export const getDirectionColor = (direction: string): string => {
  const dir = direction.toLowerCase();
  if (dir.includes('cohererend') || dir.includes('coherent')) return 'green';
  if (dir.includes('spanningsverhogend') || dir.includes('tension')) return 'orange';
  if (dir.includes('spanningsverlagend') || dir.includes('relaxing')) return 'blue';
  return 'default';
};

/**
 * Get color for amplitude tag
 */
export const getAmplitudeColor = (amplitude: string): string => {
  const amp = amplitude.toLowerCase();
  if (amp.includes('hoog') || amp.includes('high')) return 'red';
  if (amp.includes('middel') || amp.includes('medium') || amp.includes('moderate')) return 'orange';
  if (amp.includes('laag') || amp.includes('low')) return 'blue';
  return 'default';
};

/**
 * Get color for congruence tag
 */
export const getCongruenceColor = (congruence: string): string => {
  const con = congruence.toLowerCase();
  if (con.includes('congruent') && !con.includes('partieel') && !con.includes('partial')) return 'green';
  if (con.includes('partieel') || con.includes('partial')) return 'orange';
  if (con.includes('incongruent')) return 'red';
  return 'default';
};

/**
 * Translation helper
 */
export const translations = {
  // Section titles
  a1Title: { en: 'A1: Primary & Secondary Analysis', nl: 'A1: Primaire & Secundaire Analyse' },
  a3Title: { en: 'A3: Recommended Biochemical Markers', nl: 'A3: Aanbevolen Biochemische Markers' },
  a4aTitle: { en: 'A4a: Questionnaire Module Analysis', nl: 'A4a: Vragenlijst Module Analyse' },
  a4bTitle: { en: 'A4b: Hair Mineral Analysis (HMA)', nl: 'A4b: Haarmineraal Analyse (HMA)' },
  a4cTitle: { en: 'A4c: Heart Rate Variability (HRV)', nl: 'A4c: Hartslagvariabiliteit (HRV)' },
  a5Title: { en: 'A5: Pre-intake Analysis', nl: 'A5: Pre-intake Analyse' },
  
  // Common labels
  primary: { en: 'Primary:', nl: 'Primair:' },
  secondary: { en: 'Secondary:', nl: 'Secundair:' },
  teleonic: { en: 'Teleonic Pattern:', nl: 'Teleonisch Patroon:' },
  secondaryDynamics: { en: 'Secondary Dynamics:', nl: 'Secundaire Dynamica:' },
  uncertainty: { en: 'Uncertainty Level:', nl: 'Onzekerheidsniveau:' },
  directMarkers: { en: 'Direct Markers:', nl: 'Directe Markers:' },
  indirectMarkers: { en: 'Indirect Markers:', nl: 'Indirecte Markers:' },
  
  // A4a labels
  teleonicField: { en: 'Teleonic Field:', nl: 'Teleonisch Veld:' },
  direction: { en: 'Direction:', nl: 'Richting:' },
  amplitude: { en: 'Amplitude:', nl: 'Amplitude:' },
  congruence: { en: 'Congruence:', nl: 'Congruentie:' },
  hetaContext: { en: 'HETA Context:', nl: 'HETA Context:' },
  narrative: { en: 'Narrative:', nl: 'Narratief:' },
  
  // A4b labels
  eapProfile: { en: 'EAP Profile:', nl: 'EAP Profiel:' },
  eapIndex: { en: 'EAP Index:', nl: 'EAP Index:' },
  intensity: { en: 'Intensity:', nl: 'Intensiteit:' },
  buffer: { en: 'Buffer:', nl: 'Buffer:' },
  patternMarkers: { en: 'Pattern Markers:', nl: 'Patroon Markers:' },
  epistemicPositioning: { en: 'Epistemic Positioning:', nl: 'Epistemische Positionering:' },
  labReportContext: { en: 'Lab Report Context:', nl: 'Lab Rapport Context:' },
  overallPattern: { en: 'Overall Pattern:', nl: 'Algemeen Patroon:' },
  summary: { en: 'Summary:', nl: 'Samenvatting:' },
  
  // A4c labels
  rapProfile: { en: 'Recovery & Activity Profile:', nl: 'Herstel & Activiteit Profiel:' },
  rapIndex: { en: 'RAP Index:', nl: 'RAP Index:' },
  measurementContext: { en: 'Measurement Context:', nl: 'Meetcontext:' },
  globalLoadRecovery: { en: 'Global Load & Recovery:', nl: 'Globale Belasting & Herstel:' },
  nightRecovery: { en: 'Night Recovery & Sleep Rhythm:', nl: 'Nachtherstel & Slaapritme:' },
  activityDistribution: { en: 'Activity Day Distribution:', nl: 'Activiteit Verdeling:' },
  
  // A5 labels
  redFlag: { en: 'Red Flag', nl: 'Rode Vlag' },
  additionalLabAnalysis: { en: 'Additional Lab Analysis', nl: 'Aanvullende Lab Analyse' },

  // A6 labels
  adviceCore: { en: 'Core Advice:', nl: 'Kern Advies:' },
  adviceDetails: { en: 'Detailed Advice:', nl: 'Gedetailleerd Advies:' },

  // Button label
  generateSummaryReport: { en: 'Generate Summary Report', nl: 'Genereer Samenvattingsrapport' },

  // Success message
  summaryReportDownloaded: { en: 'Summary report downloaded.', nl: 'Samenvattingsrapport gedownload.' },

  // Prompt 3 labels
  prompt2Results: { en: 'Prompt 2 Results (Reference)', nl: 'Prompt 2 Resultaten (Referentie)' },
  prompt3Analysis: { en: 'Prompt 3 - Transcription Analysis', nl: 'Prompt 3 - Transcriptie Analyse' },
  prompt3Instructions: { en: 'Upload Transcription for Enhanced Analysis', nl: 'Upload Transcriptie voor Uitgebreide Analyse' },
  prompt3InstructionsDetail: { 
    en: 'Prompt 3 builds upon Prompt 2 results by analyzing a consultation transcription. Upload a PDF or Word document containing the transcription to generate enhanced insights.',
    nl: 'Prompt 3 bouwt voort op Prompt 2 resultaten door een consulttranscriptie te analyseren. Upload een PDF- of Word-document met de transcriptie om verbeterde inzichten te genereren.'
  },
  uploadTranscription: { en: 'Upload Transcription', nl: 'Upload Transcriptie' },
  clickOrDragTranscription: { en: 'Click or drag transcription file here to upload', nl: 'Klik of sleep transcriptiebestand hierheen om te uploaden' },
  transcriptionFileSupport: { en: 'Supports PDF, DOC, and DOCX files', nl: 'Ondersteunt PDF-, DOC- en DOCX-bestanden' },
  uploadTranscriptionFirst: { en: 'Please upload a transcription file first.', nl: 'Upload eerst een transcriptiebestand.' },
  generatePrompt3Analysis: { en: 'Generate Prompt 3 Analysis', nl: 'Genereer Prompt 3 Analyse' },
  prompt3AnalysisGenerated: { en: 'Prompt 3 analysis generated successfully.', nl: 'Prompt 3 analyse succesvol gegenereerd.' },
  prompt3Results: { en: 'Prompt 3 Analysis Results', nl: 'Prompt 3 Analyse Resultaten' },
};

/**
 * Get translation
 */
export type TranslationKey = keyof typeof translations;
export const t = (lang: 'en' | 'nl', key: TranslationKey): string => {
  return translations[key]?.[lang] || key;
};
